/**
 * Slideshow (revamp)
 * Ported from the dc-revamp theme's `slideshow` section (Stiletto theme.js).
 * Swiper 11 is loaded on demand from the section's `data-swiper-src`.
 */
(() => {
  if (customElements.get("slideshow-revamp")) return;

  const classes = {
    activeDot: "slideshow-navigation__dot--active",
    navigationLoader: "slideshow-navigation__dot-loader",
    visible: "is-visible"
  };

  const selectors = {
    slide: "[data-slide]",
    swiper: ".swiper",
    navigationPrev: ".slideshow-navigation__navigation-button--previous",
    navigationNext: ".slideshow-navigation__navigation-button--next",
    navigationDots: ".slideshow-navigation__dots",
    navigationDot: ".slideshow-navigation__dot",
    navigationLoader: ".slideshow-navigation__dot-loader",
    animatableItems: ".animation--section-blocks > *",
    mediaContainer: ".slideshow-slide__image-container",
    mobileMedia: ".slideshow-slide__image--mobile",
    desktopMedia: ".slideshow-slide__image:not(.slideshow-slide__image--mobile)"
  };

  let swiperModule = null;
  const loadSwiper = src => {
    if (!swiperModule) swiperModule = import(src);
    return swiperModule;
  };

  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // This theme marks keyboard navigation with `html.tab-outline`.
  const isTabbing = () =>
    document.documentElement.classList.contains("tab-outline") ||
    document.body.classList.contains("user-is-tabbing");

  // Sets `--delay-offset-multiplier` on each item so they animate in sequence.
  const delayOffset = (node, items, offsetStart = 0) => {
    let offset = offsetStart;
    items.forEach(selector => {
      node.querySelectorAll(selector).forEach(item => {
        item.style.setProperty("--delay-offset-multiplier", offset);
        offset++;
      });
    });
  };

  class SlideshowRevamp extends HTMLElement {
    connectedCallback() {
      this.slideshow = null;
      this.pendingSlide = null;
      this.navigationLoaderEls = [];
      this.enableAutoplay = this.dataset.enableAutoplay === "true";
      this.autoplayDuration = parseInt(this.dataset.autoplay, 10);
      this.slideshowContainer = this.querySelector(selectors.swiper);
      this.slides = Array.from(this.querySelectorAll(selectors.slide));

      this.handleFocus = this.handleFocus.bind(this);
      this.addEventListener("focusin", this.handleFocus);

      if (this.classList.contains("animation") && !prefersReducedMotion()) {
        this.slides.forEach(slide =>
          delayOffset(slide, [selectors.animatableItems], 3)
        );
        this.watchVisibility();
      }

      this.removeHiddenVideos();

      if (this.slides.length > 1) {
        this.initSlideshow();
      }
    }

    // When a slide has both mobile and desktop media, drop the video that isn't shown at this
    // viewport so it doesn't download and play in the background. Kept in the theme editor so
    // switching between desktop and mobile preview still works.
    removeHiddenVideos() {
      if (window.Shopify && window.Shopify.designMode) return;

      const isMobile = window.matchMedia("(max-width: 768px)").matches;

      this.querySelectorAll(selectors.mediaContainer).forEach(container => {
        const mobileMedia = container.querySelector(selectors.mobileMedia);
        if (!mobileMedia) return;

        const hiddenMedia = isMobile
          ? container.querySelector(selectors.desktopMedia)
          : mobileMedia;

        if (hiddenMedia && hiddenMedia.classList.contains("slideshow-slide__video")) {
          hiddenMedia.remove();
        }
      });
    }

    disconnectedCallback() {
      this.initToken = null;
      this.removeEventListener("focusin", this.handleFocus);
      this.visibilityObserver?.disconnect();
      this.slideshow?.destroy();
      this.slideshow = null;
    }

    // Adds `is-visible` once the section scrolls into view, which triggers the CSS entrance animation.
    watchVisibility() {
      const margin = window.matchMedia("(min-width: 720px)").matches ? 200 : 100;
      const threshold = Math.min(margin / this.offsetHeight, 0.5);

      this.visibilityObserver = new IntersectionObserver(
        ([{ isIntersecting }]) => {
          if (!isIntersecting) return;
          this.classList.add(classes.visible);
          this.visibilityObserver.disconnect();
        },
        { threshold }
      );
      this.visibilityObserver.observe(this);
    }

    async initSlideshow() {
      const token = (this.initToken = {});
      let module;

      try {
        module = await loadSwiper(this.dataset.swiperSrc);
      } catch (error) {
        console.error("Slideshow: failed to load Swiper", error);
        return;
      }

      // The section was removed (or re-rendered) while Swiper was loading.
      if (token !== this.initToken || !this.isConnected) return;

      const {
        Swiper,
        Navigation,
        Autoplay,
        Pagination,
        EffectFade,
        EffectCreative
      } = module;

      const swiperOptions = {
        modules: [Navigation, Pagination],
        autoHeight: true,
        slidesPerView: 1,
        grabCursor: true,
        effect: "fade",
        fadeEffect: {
          crossFade: false
        },
        watchSlidesProgress: true,
        loop: true,
        navigation: {
          nextEl: this.querySelector(selectors.navigationNext),
          prevEl: this.querySelector(selectors.navigationPrev)
        },
        preloadImages: false,
        pagination: {
          el: this.querySelector(selectors.navigationDots),
          clickable: true,
          bulletActiveClass: classes.activeDot,
          bulletClass: "slideshow-navigation__dot",
          renderBullet: (_, className) => `
            <button class="${className}" type="button">
              <div class="${classes.navigationLoader}"></div>
            </button>`
        },
        on: {
          afterInit: () => {
            this.handleBulletLabels();
          },
          slideChangeTransitionEnd() {
            const slideEls = this.slides;
            setTimeout(() => {
              slideEls.forEach(slide => {
                slide.toggleAttribute(
                  "inert",
                  !slide.classList.contains("swiper-slide-active")
                );
              });
            }, 50);
          }
        }
      };

      if (this.enableAutoplay) {
        swiperOptions.modules.push(Autoplay);
        swiperOptions.autoplay = {
          delay: this.autoplayDuration,
          disableOnInteraction: false
        };
      }

      if (!prefersReducedMotion()) {
        swiperOptions.modules.push(EffectFade);
        swiperOptions.effect = "fade";
        swiperOptions.fadeEffect = {
          crossFade: false
        };
      } else {
        swiperOptions.modules.push(EffectCreative);
        swiperOptions.effect = "creative";
        swiperOptions.creativeEffect = {
          prev: {
            opacity: 0.99
          },
          next: {
            opacity: 1
          }
        };
      }

      this.slideshow = new Swiper(this.slideshowContainer, swiperOptions);
      this.navigationLoaderEls = Array.from(
        this.querySelectorAll(selectors.navigationLoader)
      );

      if (this.pendingSlide !== null) {
        this.handleBlockSelect(this.pendingSlide);
        this.pendingSlide = null;
      }
    }

    resetLoaderAnimation(loader) {
      loader.classList.remove(classes.navigationLoader);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // double RAF lets the document recompute styles
          loader.classList.add(classes.navigationLoader);
        });
      });
    }

    pauseSlideshow() {
      this.slideshow?.autoplay?.stop();
      this.navigationLoaderEls.forEach(loader => {
        loader.style.animationPlayState = "paused";
      });
    }

    playSlideshow() {
      if (!this.enableAutoplay) return;

      this.slideshow?.autoplay?.start();
      this.navigationLoaderEls.forEach(loader => {
        this.resetLoaderAnimation(loader);
        loader.style.animationPlayState = "running";
      });
    }

    handleFocus() {
      if (isTabbing()) {
        this.pauseSlideshow();
      }
    }

    handleBulletLabels() {
      const bullets = this.querySelectorAll(selectors.navigationDot);
      bullets.forEach((bullet, index) => {
        const associatedSlide = this.slides[index];
        if (!associatedSlide) return;
        bullet.setAttribute("aria-label", associatedSlide.dataset.bulletLabel);
      });
    }

    handleBlockSelect(slideIndex) {
      // `data-slide` is 1-based; slideToLoop takes the 0-based real index.
      this.slideshow.slideToLoop(parseInt(slideIndex, 10) - 1);
      this.pauseSlideshow();
    }

    // Theme editor: show the selected slide and hold it while it's being edited.
    onBlockSelect(target) {
      const { slide } = target.dataset;
      if (!slide) return;

      if (this.slideshow) {
        this.handleBlockSelect(slide);
      } else {
        this.pendingSlide = slide;
      }
    }

    onBlockDeselect() {
      this.pendingSlide = null;
      this.playSlideshow();
    }
  }

  customElements.define("slideshow-revamp", SlideshowRevamp);

  document.addEventListener("shopify:block:select", event => {
    event.target.closest("slideshow-revamp")?.onBlockSelect(event.target);
  });

  document.addEventListener("shopify:block:deselect", event => {
    event.target.closest("slideshow-revamp")?.onBlockDeselect();
  });
})();
